import React, { useState, useCallback, useMemo } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Image as ImageIcon,
  Stethoscope,
  Clock,
  CheckSquare,
  Square,
  ArrowUpDown,
  Upload,
  Sun,
  Sunset,
  Moon,
  KeyRound,
  Eye,
  EyeOff,
  Gift,
  Bell,
  BadgePercent,
} from "lucide-react";
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import { StatusToggleCell } from '../components/StatusBadge';
import { useFetch, useFilters } from '../hooks';
import {
  bannerService,
  specializationService,
  qualificationService,
  timeSlotService,
  authService,
  offerBannerService,
  patientService,
  deliveryBoyService,
  couponService,
  prescriptionBannerService,
} from "../services/api";
import { formatDate } from '../utils/helpers';
import PrescriptionBannerForm from './PrescriptionBannerForm';

/* ── Tab Navigation ──────────────────────────────────────────────────────── */
const TABS = [
  { key: "banners", label: "Banners", icon: ImageIcon },
  { key: "specializations", label: "Specializations", icon: Stethoscope },
  { key: "qualifications", label: "Qualifications", icon: Stethoscope },
  { key: "change_password", label: "Change Password", icon: KeyRound },
  { key: "offer_banner", label: "Offer Banner", icon: Gift },
  { key: "user_notification", label: "Patient Notification", icon: Bell },
  // { key: "Delivery_notification", label: "Delivery Notification", icon: Bell },
  { key: "coupon", label: "Create coupon", icon: BadgePercent },
  { key: "prescription_banner", label: "Prescription Banner", icon: BadgePercent },
];

/* ── Banner Form ─────────────────────────────────────────────────────────── */
const BannerForm = ({
  initial,
  onSubmit,
  onClose,
  hideRedirectUrl = false,
}) => {
  const [imageFile, setImageFile] = useState(null);
  const { values, errors, handleChange, handleBlur, handleSubmit } = useForm(
    {
      title: initial?.title || "",
      redirect_url: initial?.redirect_url || "",
      is_active: initial?.is_active ?? 1,
    },
    {},
  );

  return (
    <Form
      onSubmit={handleSubmit((vals) =>
        onSubmit({ ...vals, panel: "user", image: imageFile }),
      )}
    >
      <FormField label="Banner Image" required={!initial}>
        <label className="flex items-center gap-2 cursor-pointer input-field py-2 text-sm text-gray-500 dark:text-gray-400 hover:border-medihub-primary transition-colors">
          <Upload className="w-4 h-4 shrink-0 text-gray-400" />
          <span className="truncate">
            {imageFile
              ? imageFile.name
              : initial?.image_url
                ? "Change image"
                : "Upload image…"}
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            className="hidden"
          />
        </label>
        {initial?.image_url && !imageFile && (
          <img
            src={initial.image_url}
            alt=""
            className="mt-2 h-20 rounded-lg object-cover border border-gray-200 dark:border-gray-700"
          />
        )}
      </FormField>
      <FormField label="Title">
        <input
          name="title"
          value={values.title}
          onChange={handleChange}
          className="input-field"
          placeholder="Banner title (optional)"
        />
      </FormField>
      {!hideRedirectUrl && (
        <FormField label="Redirect URL">
          <input
            name="redirect_url"
            value={values.redirect_url}
            onChange={handleChange}
            className="input-field"
            placeholder="https://… (optional)"
          />
        </FormField>
      )}
      <FormField label="Status">
        <select
          name="is_active"
          value={values.is_active}
          onChange={handleChange}
          className="input-field"
        >
          <option value={1}>Active</option>
          <option value={0}>Inactive</option>
        </select>
      </FormField>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary">
          {initial ? "Update" : "Add Banner"}
        </button>
      </div>
    </Form>
  );
};

/* ── Specialization Form ─────────────────────────────────────────────────── */
const SpecializationForm = ({ initial, existingNames, onSubmit, onClose }) => {
  const rules = { name: { required: true } };
  const { values, errors, handleChange, handleBlur, handleSubmit } = useForm(
    initial ? { name: initial.name } : { name: '' },
    rules
  );

  const handleFormSubmit = (vals) => {
    const trimmed = vals.name.trim();
    const duplicate = existingNames
      .filter(n => !initial || n.toLowerCase() !== initial.name.toLowerCase())
      .some(n => n.toLowerCase() === trimmed.toLowerCase());
    if (duplicate) { toast.error('Specialization already exists'); return; }
    onSubmit({ ...vals, name: trimmed });
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <FormField label="Specialization Name" error={errors.name} required>
        <input name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} className="input-field" placeholder="e.g. Cardiologist" autoFocus />
      </FormField>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">{initial ? 'Update' : 'Add Specialization'}</button>
      </div>
    </Form>
  );
};

/* ── Time Slot Form ──────────────────────────────────────────────────────── */
const to12h = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
};

const formatSlotLabel = (start, end) => start && end ? `${to12h(start)} – ${to12h(end)}` : '';

const TimeSlotForm = ({ initial, onSubmit, onClose }) => {
  const [overlapError, setOverlapError] = useState('');
  const rules = { startTime: { required: true }, endTime: { required: true } };
  const { values, errors, handleChange, handleBlur, handleSubmit } = useForm(
    initial ? { startTime: initial.startTime, endTime: initial.endTime } : { startTime: '', endTime: '' },
    rules
  );

  const previewLabel = formatSlotLabel(values.startTime, values.endTime);

  const handleFormSubmit = (vals) => {
    setOverlapError('');
    if (vals.endTime <= vals.startTime) { setOverlapError('End time must be after start time'); return; }
    const hasOverlap = timeSlotService.checkOverlap(vals.startTime, vals.endTime, initial?.id);
    if (hasOverlap) { setOverlapError('This time slot overlaps with an existing slot'); return; }
    const label = formatSlotLabel(vals.startTime, vals.endTime);
    onSubmit({ ...vals, label });
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Start Time" error={errors.startTime} required>
          <input name="startTime" type="time" value={values.startTime} onChange={handleChange} onBlur={handleBlur} className="input-field" />
        </FormField>
        <FormField label="End Time" error={errors.endTime} required>
          <input name="endTime" type="time" value={values.endTime} onChange={handleChange} onBlur={handleBlur} className="input-field" />
        </FormField>
      </div>
      {previewLabel && (
        <div className="px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
          <p className="text-xs text-gray-500 dark:text-gray-400">Preview</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{previewLabel}</p>
        </div>
      )}
      {overlapError && <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">{overlapError}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">{initial ? 'Update' : 'Add Time Slot'}</button>
      </div>
    </Form>
  );
};

/* ── Banners Tab ─────────────────────────────────────────────────────────── */
const BannersTab = () => {
  const { data: banners, loading, refetch } = useFetch(() => bannerService.getAll());
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleSave = async (vals) => {
    try {
      if (modal === 'edit') {
        const res = await bannerService.update(selected.id, vals);
        toast.success(res?.message || 'Banner updated!');
      } else {
        const res = await bannerService.create({ ...vals, is_active: 1 });
        toast.success(res?.message || 'Banner added!');
      }
      refetch(); setModal(null); setSelected(null);
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async () => {
    try {
      const res = await bannerService.delete(deleteTarget.id);
      toast.success(res?.message || 'Banner deleted');
      setDeleteTarget(null); refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to delete'); }
  };

  const handleToggle = async (banner) => {
    const newStatus = banner.is_active ? 0 : 1;
    try {
      await bannerService.toggleStatus(banner.id, newStatus);
      toast.success('Status updated');
      refetch();
    } catch { toast.error('Failed'); }
  };

  const columns = [
    {
      key: 'image_url', label: 'Image', render: v => v ? (
        <img src={v} alt="" className="w-16 h-10 rounded-lg object-cover border border-gray-200 dark:border-gray-700" />
      ) : <div className="w-16 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center"><ImageIcon className="w-4 h-4 text-gray-400" /></div>
    },
    { key: 'title', label: 'Title', render: v => <span className="font-medium text-gray-900 dark:text-white">{v || '—'}</span> },
    { key: 'panel', label: 'Panel', render: v => <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium capitalize bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{v || '—'}</span> },
    { key: 'redirect_url', label: 'Redirect URL', render: v => v ? <a href={v} target="_blank" rel="noreferrer" className="text-xs text-medihub-primary dark:text-medihub-highlight hover:underline truncate max-w-[120px] block">{v}</a> : <span className="text-gray-400 text-xs">—</span> },
    { key: 'sort_order', label: 'Order', render: v => <span className="text-sm text-gray-500">{v ?? '—'}</span> },
    {
      key: 'is_active', label: 'Status', render: (v, row) => (
        <StatusToggleCell active={!!v} onToggle={() => handleToggle(row)} />
      )
    },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => { setSelected(row); setModal('edit'); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" title="Edit">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => setDeleteTarget(row)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage promotional banners</p>
        <button onClick={() => { setSelected(null); setModal('add'); }} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Add Banner
        </button>
      </div>
      <Table title={`Banners (${banners?.length || 0})`} columns={columns} data={banners || []} loading={loading} exportFilename="banners" />

      <Modal isOpen={modal === 'add' || modal === 'edit'} onClose={() => { setModal(null); setSelected(null); }} title={modal === 'edit' ? 'Edit Banner' : 'Add Banner'} size="md">
        <BannerForm initial={modal === 'edit' ? selected : null} onSubmit={handleSave} onClose={() => { setModal(null); setSelected(null); }} />
      </Modal>

      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Banner" size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400">Are you sure you want to delete this banner?</p>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={() => setDeleteTarget(null)} className="btn-secondary">Cancel</button>
          <button onClick={handleDelete} className="btn-danger">Delete</button>
        </div>
      </Modal>
    </>
  );
};

/* ── Offer Banner Tab ───────────────────────────────────────────── */
const OfferBannerTab = () => {
  const { data: banners, loading, refetch } = useFetch(() =>
    bannerService.getOfferBanners()
  );

  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleSave = async (vals) => {
    try {
      if (modal === 'edit') {
        await bannerService.updateOfferBanner(selected.id, vals);
        toast.success('Offer banner updated!');
      } else {
        await bannerService.createOfferBanner({
          ...vals,
          panel: 'offer',
        });
        toast.success('Offer banner added!');
      }

      refetch();
      setModal(null);
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed');
    }
  };

  const handleDelete = async () => {
    try {
      await bannerService.deleteOfferBanner(deleteTarget.id);
      toast.success('Offer banner deleted');
      setDeleteTarget(null);
      refetch();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (banner) => {
    try {
      await bannerService.toggleStatus(
        banner.id,
        banner.is_active ? 0 : 1
      );

      toast.success('Status updated');
      refetch();
    } catch {
      toast.error('Failed');
    }
  };

const handleStatusChange = async (banner) => {
  try {
    await offerBannerService.toggleStatus(banner.id, banner.is_active ? 0 : 1);

    toast.success("Status updated");
    refetch();
  } catch (error) {
    toast.error("Failed to update status");
  }
};

  const columns = [
    {
      key: "image_url",
      label: "Image",
      render: (v) =>
        v ? (
          <img
            src={v}
            alt=""
            className="w-16 h-10 rounded-lg object-cover border"
          />
        ) : (
          <div className="w-16 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <ImageIcon className="w-4 h-4 text-gray-400" />
          </div>
        ),
    },

    {
      key: "title",
      label: "Title",
    },

    // {
    //   key: "redirect_url",
    //   label: "Redirect URL",
    //   render: (v) =>
    //     v ? (
    //       <a
    //         href={v}
    //         target="_blank"
    //         rel="noreferrer"
    //         className="text-medihub-primary text-xs hover:underline"
    //       >
    //         {v}
    //       </a>
    //     ) : (
    //       "—"
    //     ),
    // },

    {
      key: "is_active",
      label: "Status",
      render: (v, row) => (
        <StatusToggleCell
          active={!!v}
          onToggle={() => handleStatusChange(row)}
        />
      ),
    },

    {
      key: "id",
      label: "Actions",
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              setSelected(row);
              setModal("edit");
            }}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
          >
            <Edit2 className="w-4 h-4" />
          </button>

          <button
            onClick={() => setDeleteTarget(row)}
            className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Manage offer banners</p>

        <button
          onClick={() => {
            setSelected(null);
            setModal("add");
          }}
          className="btn-primary flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" />
          Add Offer Banner
        </button>
      </div>

      <Table
        title={`Offer Banners (${banners?.length || 0})`}
        columns={columns}
        data={banners || []}
        loading={loading}
      />

      <Modal
        isOpen={modal === "add" || modal === "edit"}
        onClose={() => {
          setModal(null);
          setSelected(null);
        }}
        title={modal === "edit" ? "Edit Offer Banner" : "Add Offer Banner"}
      >
        <BannerForm
          initial={modal === "edit" ? selected : null}
          onSubmit={handleSave}
          onClose={() => {
            setModal(null);
            setSelected(null);
          }}
          hideRedirectUrl={true}
        />
      </Modal>

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Offer Banner"
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to delete this offer banner?
        </p>

        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={() => setDeleteTarget(null)}
            className="btn-secondary"
          >
            Cancel
          </button>

          <button onClick={handleDelete} className="btn-danger">
            Delete
          </button>
        </div>
      </Modal>
    </>
  );
};

// ------------------prescription banner tab ------------------ 
const PrescriptionBanner = () => {
  const {
    data: banners,
    loading,
    refetch,
  } = useFetch(() => prescriptionBannerService.getAll());
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleSave = async (vals) => {
    try {
      if (modal === "edit") {
        const res = await prescriptionBannerService.update(selected.id, vals);
        toast.success(res?.message || "Banner updated!");
      } else {
        const res = await prescriptionBannerService.create(vals);
        toast.success(res?.message || "Banner added!");
      }
      refetch();
      setModal(null);
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await prescriptionBannerService.delete(deleteTarget.id);
      toast.success(res?.message || "Banner deleted");
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete");
    }
  };

  const handleToggle = async (banner) => {
    try {
      await prescriptionBannerService.toggleStatus(
        banner.id,
        banner.is_active ? 0 : 1,
      );
      toast.success("Status updated");
      refetch();
    } catch {
      toast.error("Failed");
    }
  };

  const columns = [
    {
      key: "image_url",
      label: "Banner Image",
      render: (v) =>
        v ? (
          <img
            src={v}
            alt="Banner"
            className="w-24 h-14 rounded-lg object-cover border border-gray-200 dark:border-gray-700"
          />
        ) : (
          <div className="w-24 h-14 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-gray-400" />
          </div>
        ),
    },
    {
      key: "contact_number",
      label: "WhatsApp Number",
      render: (v) => (
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-green-500 shrink-0">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <span className="font-medium text-gray-800 dark:text-gray-200 text-sm">
            {v || "—"}
          </span>
          {v && (
            <a
              href={`https://wa.me/${v}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-green-600 hover:underline font-semibold"
            >
              Open ↗
            </a>
          )}
        </div>
      ),
    },
    {
      key: "is_active",
      label: "Status",
      render: (v, row) => (
        <StatusToggleCell active={!!v} onToggle={() => handleToggle(row)} />
      ),
    },
    {
      key: "created_at",
      label: "Added On",
      render: (v) => (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {v
            ? new Date(v).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "—"}
        </span>
      ),
    },
    {
      key: "id",
      label: "Actions",
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              setSelected(row);
              setModal("edit");
            }}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Manage prescription banners shown to users at checkout
        </p>
        <button
          onClick={() => {
            setSelected(null);
            setModal("add");
          }}
          className="btn-primary flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Add Banner
        </button>
      </div>

      <Table
        title={`Prescription Banners (${banners?.length || 0})`}
        columns={columns}
        data={banners || []}
        loading={loading}
        exportFilename="prescription-banners"
      />

      {/* Add / Edit Modal */}
      <Modal
        isOpen={modal === "add" || modal === "edit"}
        onClose={() => {
          setModal(null);
          setSelected(null);
        }}
        title={
          modal === "edit"
            ? "Edit Prescription Banner"
            : "Add Prescription Banner"
        }
        size="md"
      >
        <PrescriptionBannerForm
          initial={modal === "edit" ? selected : null}
          onSubmit={handleSave}
          onClose={() => {
            setModal(null);
            setSelected(null);
          }}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Banner"
        size="sm"
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Are you sure you want to delete this prescription banner?
        </p>
        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={() => setDeleteTarget(null)}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button onClick={handleDelete} className="btn-danger">
            Delete
          </button>
        </div>
      </Modal>
    </>
  );
};

/* ── Coupon Tab ──────────────────────────────────────────────────────────── */
const CouponTab = () => {
  const { data: coupons, loading, refetch } = useFetch(() => couponService.getAll());
  const [modal, setModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    title: '',
    discount_type: 'percentage',
    discount_value: '',
    user_type: 'limited',
    max_users: '',
    specific_user_id: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm({
      title: '',
      discount_type: 'percentage',
      discount_value: '',
      user_type: 'limited',
      max_users: '',
      specific_user_id: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }
    if (!form.discount_value) { toast.error('Discount value is required'); return; }
    if (form.user_type === 'limited' && !form.max_users) {
      toast.error('Max users is required for limited type'); return;
    }
    setFormLoading(true);
    try {
      const payload = {
        title: form.title,
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        user_type: form.user_type,
        max_users: form.user_type === 'limited' ? Number(form.max_users) : null,
        specific_user_id: form.specific_user_id || null,
      };
      const res = await couponService.create(payload);
      toast.success(res?.message || 'Coupon created successfully!');
      setModal(false);
      resetForm();
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create coupon');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await couponService.delete(deleteTarget.id);
      toast.success(res?.message || "Coupon deleted successfully!");
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete coupon");
    }
  };
  const columns = [
    {
      key: "coupon_code",
      label: "Coupon Code",
      render: (v) => (
        <span className="font-mono font-bold text-medihub-primary bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 rounded-lg text-sm tracking-widest">
          {v}
        </span>
      ),
    },
    {
      key: "title",
      label: "Title",
      render: (v) => (
        <span className="font-medium text-gray-900 dark:text-white">{v}</span>
      ),
    },
    {
      key: "discount_type",
      label: "Type",
      render: (v) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold capitalize
          ${v === "percentage" ? "bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300" : "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300"}`}
        >
          {v === "percentage" ? "%" : "₹"} {v}
        </span>
      ),
    },
    {
      key: "discount_value",
      label: "Discount",
      render: (v, row) => (
        <span className="font-semibold text-gray-800 dark:text-gray-200">
          {row.discount_type === "percentage"
            ? `${parseFloat(v)}%`
            : `₹${parseFloat(v)}`}
        </span>
      ),
    },
    {
      key: "user_type",
      label: "User Type",
      render: (v, row) => (
        <div>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold capitalize
            ${v === "limited" ? "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300" : "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"}`}
          >
            {v}
          </span>
          {v === "limited" && row.max_users && (
            <p className="text-xs text-gray-400 mt-0.5">
              Max: {row.max_users} users
            </p>
          )}
        </div>
      ),
    },
    {
      key: "is_active",
      label: "Status",
      render: (v) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold
          ${v === 1 ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300" : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"}`}
        >
          {v === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "Created",
      render: (v) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatDate(v)}
        </span>
      ),
    },
    {
      key: "id",
      label: "Actions",
      render: (_, row) => (
        <button
          onClick={() => setDeleteTarget(row)}
          className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Manage discount coupons
        </p>
        <button
          onClick={() => {
            resetForm();
            setModal(true);
          }}
          className="btn-primary flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Create Coupon
        </button>
      </div>

      <Table
        title={`Coupons (${coupons?.length || 0})`}
        columns={columns}
        data={coupons || []}
        loading={loading}
        exportFilename="coupons"
      />

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Coupon"
        size="sm"
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Are you sure you want to delete coupon{" "}
          <span className="font-mono font-bold text-medihub-primary">
            {deleteTarget?.coupon_code}
          </span>
          ? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={() => setDeleteTarget(null)}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button onClick={handleDelete} className="btn-danger">
            Delete
          </button>
        </div>
      </Modal>

      {/* Create Coupon Modal */}
      <Modal
        isOpen={modal}
        onClose={() => {
          setModal(false);
          resetForm();
        }}
        title="Create Coupon"
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <FormField label="Title" required>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              className="input-field"
              placeholder="e.g. Flat 10% OFF"
            />
          </FormField>

          {/* Discount Type + Value — side by side */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Discount Type" required>
              <select
                name="discount_type"
                value={form.discount_type}
                onChange={handleChange}
                className="input-field"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed (₹)</option>
              </select>
            </FormField>

            <FormField
              label={`Discount Value (${form.discount_type === "percentage" ? "%" : "₹"})`}
              required
            >
              <input
                type="number"
                name="discount_value"
                value={form.discount_value}
                onChange={handleChange}
                className="input-field"
                placeholder={
                  form.discount_type === "percentage" ? "e.g. 10" : "e.g. 50"
                }
                min="1"
                max={form.discount_type === "percentage" ? 100 : undefined}
              />
            </FormField>
          </div>

          {/* User Type */}
          <FormField label="User Type" required>
            <select
              name="user_type"
              value={form.user_type}
              onChange={handleChange}
              className="input-field"
            >
              <option value="limited">Limited</option>
              <option value="all">All Users</option>
              <option value="specific">Specific User</option>
            </select>
          </FormField>

          {/* Max Users — sirf limited ke liye */}
          {form.user_type === "limited" && (
            <FormField label="Max Users" required>
              <input
                type="number"
                name="max_users"
                value={form.max_users}
                onChange={handleChange}
                className="input-field"
                placeholder="e.g. 10"
                min="1"
              />
            </FormField>
          )}

          {/* Specific User ID — sirf specific ke liye */}
          {form.user_type === "specific" && (
            <FormField label="Specific User ID" required>
              <input
                type="number"
                name="specific_user_id"
                value={form.specific_user_id || ""}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    specific_user_id: e.target.value || null,
                  }))
                }
                className="input-field"
                placeholder="Enter user ID"
                min="1"
              />
            </FormField>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setModal(false);
                resetForm();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formLoading}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {formLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" /> Create Coupon
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};
/* ── Specializations Tab ─────────────────────────────────────────────────── */
const SpecializationsTab = () => {
  const { data: specs, loading, refetch } = useFetch(() => specializationService.getAll());
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sortAZ, setSortAZ] = useState(true);

  const sorted = useMemo(() => {
    if (!specs) return [];
    return [...specs].sort((a, b) => sortAZ ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));
  }, [specs, sortAZ]);

  const existingNames = specs?.map(s => s.name) || [];

  const handleSave = async (vals) => {
    try {
      if (modal === 'edit') {
        await specializationService.update(selected.id, vals);
        toast.success('Specialization updated!');
      } else {
        const res = await specializationService.create({ name: vals.name });
        toast.success(res?.message || 'Specialization added!');
      }
      refetch(); setModal(null); setSelected(null);
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    const spec = specs?.find(s => s.id === id);
    if (spec?.doctorCount > 0) { toast.error(`Cannot delete — ${spec.doctorCount} doctor(s) assigned`); return; }
    if (!window.confirm('Delete this specialization?')) return;
    try { await specializationService.delete(id); toast.success('Deleted'); refetch(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (spec) => {
    try { await specializationService.toggleStatus(spec.id); toast.success('Status updated'); refetch(); }
    catch { toast.error('Failed'); }
  };

  const columns = [
    {
      key: "name",
      label: "Specialization",
      render: (v) => (
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-medihub-light dark:bg-medihub-dark/40 rounded-lg flex items-center justify-center shrink-0">
            <Stethoscope className="w-4 h-4 text-medihub-primary dark:text-medihub-highlight" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">{v}</span>
        </div>
      ),
    },
    // { key: 'doctorCount', label: 'Doctors', render: v => <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300">{v}</span> },
    {
      key: "created_at",
      label: "Created",
      render: (v) => (
        <span className="text-sm text-gray-500">{formatDate(v)}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (v, row) => (
        <StatusToggleCell
          active={v === "active"}
          onToggle={() => handleToggle(row)}
        />
      ),
    },
    // {
    //   key: 'id', label: 'Actions', render: (_, row) => (
    //     <div className="flex items-center gap-1">
    //       <button onClick={() => { setSelected(row); setModal('edit'); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" title="Edit">
    //         <Edit2 className="w-4 h-4" />
    //       </button>
    //       <button onClick={() => handleDelete(row.id)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Delete">
    //         <Trash2 className="w-4 h-4" />
    //       </button>
    //     </div>
    //   )
    // },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage doctor specializations</p>
          <button onClick={() => setSortAZ(v => !v)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-medihub-primary transition-colors">
            <ArrowUpDown className="w-3.5 h-3.5" />{sortAZ ? 'A → Z' : 'Z → A'}
          </button>
        </div>
        <button onClick={() => { setSelected(null); setModal('add'); }} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Add Specialization
        </button>
      </div>
      <Table title={`Specializations (${sorted.length})`} columns={columns} data={sorted} loading={loading} exportFilename="specializations" />

      <Modal isOpen={modal === 'add' || modal === 'edit'} onClose={() => { setModal(null); setSelected(null); }} title={modal === 'edit' ? 'Edit Specialization' : 'Add Specialization'} size="sm">
        <SpecializationForm initial={modal === 'edit' ? selected : null} existingNames={existingNames} onSubmit={handleSave} onClose={() => { setModal(null); setSelected(null); }} />
      </Modal>
    </>
  );
};

/* ── Qualifications Tab ────────────────────────────────────────── */
const QualificationsTab = () => {
  const { data: quals, loading, refetch } = useFetch(() => qualificationService.getAll());
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sortAZ, setSortAZ] = useState(true);

  const sorted = useMemo(() => {
    if (!quals) return [];
    return [...quals].sort((a, b) => sortAZ ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));
  }, [quals, sortAZ]);

  const existingNames = quals?.map(q => q.name) || [];

  const handleSave = async (vals) => {
    try {
      if (modal === 'edit') {
        await qualificationService.update(selected.id, vals);
        toast.success('Qualification updated!');
      } else {
        const res = await qualificationService.create(vals.name);
        toast.success(res?.message || 'Qualification added!');
      }
      refetch(); setModal(null); setSelected(null);
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this qualification?')) return;
    try { await qualificationService.delete(id); toast.success('Deleted'); refetch(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (qual) => {
    try { await qualificationService.toggleStatus(qual.id); toast.success('Status updated'); refetch(); }
    catch { toast.error('Failed'); }
  };

  const columns = [
    {
      key: 'name', label: 'Qualification', render: v => (
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center shrink-0">
            <Stethoscope className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">{v}</span>
        </div>
      )
    },
    { key: 'created_at', label: 'Created', render: v => <span className="text-sm text-gray-500">{formatDate(v)}</span> },
    {
      key: 'status', label: 'Status', render: (v, row) => (
        <StatusToggleCell active={v === 'active'} onToggle={() => handleToggle(row)} />
      )
    },
    // {
    //   key: 'id', label: 'Actions', render: (_, row) => (
    //     <div className="flex items-center gap-1">
    //       <button onClick={() => { setSelected(row); setModal('edit'); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" title="Edit">
    //         <Edit2 className="w-4 h-4" />
    //       </button>
    //       <button onClick={() => handleDelete(row.id)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Delete">
    //         <Trash2 className="w-4 h-4" />
    //       </button>
    //     </div>
    //   )
    // },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage doctor qualifications</p>
          <button onClick={() => setSortAZ(v => !v)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-medihub-primary transition-colors">
            <ArrowUpDown className="w-3.5 h-3.5" />{sortAZ ? 'A → Z' : 'Z → A'}
          </button>
        </div>
        <button onClick={() => { setSelected(null); setModal('add'); }} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Add Qualification
        </button>
      </div>
      <Table title={`Qualifications (${sorted.length})`} columns={columns} data={sorted} loading={loading} exportFilename="qualifications" />

      <Modal isOpen={modal === 'add' || modal === 'edit'} onClose={() => { setModal(null); setSelected(null); }} title={modal === 'edit' ? 'Edit Qualification' : 'Add Qualification'} size="sm">
        <SpecializationForm initial={modal === 'edit' ? selected : null} existingNames={existingNames} onSubmit={handleSave} onClose={() => { setModal(null); setSelected(null); }} />
      </Modal>
    </>
  );
};

/* ── Notification Form ─────────────────────────────────────────── */

// const NotificationTab = ({ type }) => {
//   const [loading, setLoading] = useState(false);
//   const [patientSearch, setPatientSearch] = useState("");

//   const { data: patients } = useFetch(() => patientService.getAll());

//   const [form, setForm] = useState({
//     send_type: "all",
//     user_ids: [],
//     title: "",
//     description: "",
//   });

//   // ── Age Filter States ─────────────────────────
//   const [selectedRange, setSelectedRange] = useState("");
//   const [customAge, setCustomAge] = useState({
//     start: "",
//     end: "",
//   });

//   // ── Calculate Age ─────────────────────────────
//   const calculateAge = (dob) => {
//     if (!dob) return "";

//     const birthDate = new Date(dob);
//     const today = new Date();

//     let age = today.getFullYear() - birthDate.getFullYear();

//     const monthDiff = today.getMonth() - birthDate.getMonth();

//     if (
//       monthDiff < 0 ||
//       (monthDiff === 0 && today.getDate() < birthDate.getDate())
//     ) {
//       age--;
//     }

//     return age;
//   };

//   // ── Apply Age Filter ──────────────────────────
//   const applyAgeFilter = () => {
//     let startAge = 0;
//     let endAge = 200;

//     // dropdown ranges
//     if (selectedRange === "0-18") {
//       startAge = 0;
//       endAge = 18;
//     } else if (selectedRange === "19-30") {
//       startAge = 19;
//       endAge = 30;
//     } else if (selectedRange === "31-45") {
//       startAge = 31;
//       endAge = 45;
//     } else if (selectedRange === "46-60") {
//       startAge = 46;
//       endAge = 60;
//     } else if (selectedRange === "60+") {
//       startAge = 60;
//       endAge = 200;
//     }

//     // custom range
//     if (selectedRange === "custom") {
//       startAge = Number(customAge.start || 0);
//       endAge = Number(customAge.end || 200);
//     }

//     const filteredPatientIds =
//       patients
//         ?.filter((patient) => {
//           const age = calculateAge(patient.dob);

//           return age >= startAge && age <= endAge;
//         })
//         .map((patient) => patient.id) || [];

//     setForm((prev) => ({
//       ...prev,
//       user_ids: filteredPatientIds,
//     }));

//     toast.success(
//       `${filteredPatientIds.length} patients selected successfully`
//     );
//   };

//   // ── Submit ────────────────────────────────────
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (
//       form.send_type === "selected" &&
//       form.user_ids.length === 0
//     ) {
//       toast.error("Please select patients");
//       return;
//     }

//     if (!form.title) {
//       toast.error("Title is required");
//       return;
//     }

//     if (!form.description) {
//       toast.error("Description is required");
//       return;
//     }

//     setLoading(true);

//     try {
//       const payload = {
//         title: form.title,
//         description: form.description,
//         send_type: form.send_type,
//       };

//       if (form.send_type === "selected") {
//         payload.user_ids = form.user_ids;
//       }

//       if (type === "patient") {
//         await authService.sendPatientNotification(payload);
//       } else {
//         await authService.sendDeliveryNotification(payload);
//       }

//       toast.success("Notification sent successfully");

//       setForm({
//         send_type: "all",
//         user_ids: [],
//         title: "",
//         description: "",
//       });

//       setSelectedRange("");
//       setCustomAge({
//         start: "",
//         end: "",
//       });
//     } catch (err) {
//       toast.error(
//         err?.response?.data?.message ||
//           "Failed to send notification"
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   const filteredPatients =
//     patients?.filter((patient) => {
//       const search = patientSearch.toLowerCase();

//       return (
//         patient.name?.toLowerCase().includes(search) ||
//         String(patient.id).includes(search)
//       );
//     }) || [];

//   return (
//     <div className="max-w-xl">
//       <p className="text-sm text-gray-500 mb-5">Send notification to {type}</p>

//       <form onSubmit={handleSubmit} className="space-y-4">
//         {/* Send Type */}
//         <FormField label="Send Type" required>
//           <select
//             value={form.send_type}
//             onChange={(e) =>
//               setForm((p) => ({
//                 ...p,
//                 send_type: e.target.value,
//               }))
//             }
//             className="input-field"
//           >
//             <option value="all">All Users</option>
//             <option value="selected">Selected Users</option>
//           </select>
//         </FormField>

//         {/* Selected Users */}
//         {form.send_type === "selected" && (
//           <>
//             {/* Age Filter */}
//             <div className="p-4 border rounded-xl space-y-4 bg-gray-50 dark:bg-gray-800">
//               <h3 className="font-medium text-sm">Filter Patients By Age</h3>

//               {/* Dropdown */}
//               <FormField label="Age Group">
//                 <select
//                   value={selectedRange}
//                   onChange={(e) => setSelectedRange(e.target.value)}
//                   className="input-field"
//                 >
//                   <option value="">Select Age Group</option>
//                   <option value="0-18">0 - 18 Years</option>
//                   <option value="19-30">19 - 30 Years</option>
//                   <option value="31-45">31 - 45 Years</option>
//                   <option value="46-60">46 - 60 Years</option>
//                   <option value="60+">60+ Years</option>
//                   <option value="custom">Others (Custom)</option>
//                 </select>
//               </FormField>

//               {/* Custom Range */}
//               {selectedRange === "custom" && (
//                 <div className="grid grid-cols-2 gap-3">
//                   <FormField label="Start Age">
//                     <input
//                       type="number"
//                       placeholder="e.g. 20"
//                       value={customAge.start}
//                       onChange={(e) =>
//                         setCustomAge((p) => ({
//                           ...p,
//                           start: e.target.value,
//                         }))
//                       }
//                       className="input-field"
//                     />
//                   </FormField>

//                   <FormField label="End Age">
//                     <input
//                       type="number"
//                       placeholder="e.g. 40"
//                       value={customAge.end}
//                       onChange={(e) =>
//                         setCustomAge((p) => ({
//                           ...p,
//                           end: e.target.value,
//                         }))
//                       }
//                       className="input-field"
//                     />
//                   </FormField>
//                 </div>
//               )}

//               {/* Apply Button */}
//               <button
//                 type="button"
//                 onClick={applyAgeFilter}
//                 className="btn-primary"
//               >
//                 Select Patients By Age
//               </button>
//             </div>
//             <FormField label="Search Patient">
//               <input
//                 type="text"
//                 placeholder="Search by patient name or ID"
//                 value={patientSearch}
//                 onChange={(e) => setPatientSearch(e.target.value)}
//                 className="input-field"
//               />
//             </FormField>

//             {/* Patient Dropdown */}
//             <FormField label="Select Patients" required>
//               <select
//                 multiple
//                 value={form.user_ids}
//                 onChange={(e) => {
//                   const selectedIds = Array.from(
//                     e.target.selectedOptions,
//                     (option) => Number(option.value),
//                   );

//                   setForm((p) => ({
//                     ...p,
//                     user_ids: selectedIds,
//                   }));
//                 }}
//                 className="input-field h-52"
//               >
//                 {filteredPatients.map((patient) => (
//                   <option key={patient.id} value={patient.id}>
//                     {patient.name} ({calculateAge(patient.dob)} yrs)
//                   </option>
//                 ))}
//               </select>

//               <p className="text-xs text-gray-500 mt-1">
//                 Hold Ctrl (Windows) / Command (Mac) to select multiple patients
//               </p>
//             </FormField>
//           </>
//         )}

//         {/* Title */}
//         <FormField label="Title" required>
//           <input
//             type="text"
//             placeholder="Enter title"
//             value={form.title}
//             onChange={(e) =>
//               setForm((p) => ({
//                 ...p,
//                 title: e.target.value,
//               }))
//             }
//             className="input-field"
//           />
//         </FormField>

//         {/* Description */}
//         <FormField label="Description" required>
//           <textarea
//             rows={4}
//             placeholder="Enter notification description"
//             value={form.description}
//             onChange={(e) =>
//               setForm((p) => ({
//                 ...p,
//                 description: e.target.value,
//               }))
//             }
//             className="input-field"
//           />
//         </FormField>

//         {/* Submit */}
//         <button
//           type="submit"
//           disabled={loading}
//           className="btn-primary flex items-center gap-2"
//         >
//           <Bell className="w-4 h-4" />
//           {loading ? "Sending..." : "Send Notification"}
//         </button>
//       </form>
//     </div>
//   );
// };

const NotificationTab = ({ type }) => {
  const [loading, setLoading] = useState(false);

  // ── Dynamic API Based On Type ─────────────────────
  const { data: users } = useFetch(() =>
    type === "patient" ? patientService.getAll() : deliveryBoyService.getAll(),
  );

  const [patientSearch, setPatientSearch] = useState("");

  const [form, setForm] = useState({
    send_type: "all",
    user_ids: [],
    title: "",
    description: "",
  });

  // ── Age Filter States ─────────────────────────────
  const [selectedRange, setSelectedRange] = useState("");
  const [customAge, setCustomAge] = useState({
    start: "",
    end: "",
  });

  // ── Calculate Age ─────────────────────────────────
  const calculateAge = (dob) => {
    if (!dob) return "";

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  // ── Search Filter ────────────────────────────────
  const filteredUsers =
    users?.filter((user) => {
      const search = patientSearch.toLowerCase();

      return (
        user.name?.toLowerCase().includes(search) ||
        String(user.id).includes(search)
      );
    }) || [];

  // ── Apply Age Filter (Only Patients) ─────────────
  const applyAgeFilter = () => {
    let startAge = 0;
    let endAge = 200;

    if (selectedRange === "0-18") {
      startAge = 0;
      endAge = 18;
    } else if (selectedRange === "19-30") {
      startAge = 19;
      endAge = 30;
    } else if (selectedRange === "31-45") {
      startAge = 31;
      endAge = 45;
    } else if (selectedRange === "46-60") {
      startAge = 46;
      endAge = 60;
    } else if (selectedRange === "60+") {
      startAge = 60;
      endAge = 200;
    }

    if (selectedRange === "custom") {
      startAge = Number(customAge.start || 0);
      endAge = Number(customAge.end || 200);
    }

    const filteredIds =
      users
        ?.filter((user) => {
          const age = calculateAge(user.dob);

          return age >= startAge && age <= endAge;
        })
        .map((user) => user.id) || [];

    setForm((prev) => ({
      ...prev,
      user_ids: filteredIds,
    }));

    toast.success(`${filteredIds.length} users selected`);
  };

  // ── Submit ───────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.send_type === "selected" && form.user_ids.length === 0) {
      toast.error("Please select users");
      return;
    }

    if (!form.title) {
      toast.error("Title is required");
      return;
    }

    if (!form.description) {
      toast.error("Description is required");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        title: form.title,
        description: form.description,
        send_type: form.send_type,
      };

      if (form.send_type === "selected") {
        payload.user_ids = form.user_ids;
      }

      if (type === "patient") {
        await authService.sendPatientNotification(payload);
      } else {
        await authService.sendDeliveryNotification(payload);
      }

      toast.success("Notification sent successfully");

      setForm({
        send_type: "all",
        user_ids: [],
        title: "",
        description: "",
      });

      setPatientSearch("");

      setSelectedRange("");

      setCustomAge({
        start: "",
        end: "",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to send notification",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl">
      <p className="text-sm text-gray-500 mb-5">Send notification to {type}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Send Type */}
        <FormField label="Send Type" required>
          <select
            value={form.send_type}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                send_type: e.target.value,
              }))
            }
            className="input-field"
          >
            <option value="all">All Users</option>
            <option value="selected">Selected Users</option>
          </select>
        </FormField>

        {/* Selected Users */}
        {form.send_type === "selected" && (
          <>
            {/* Age Filter Only For Patients */}
            {type === "patient" && (
              <div className="p-4 border rounded-xl space-y-4 bg-gray-50 dark:bg-gray-800">
                <h3 className="font-medium text-sm">Filter Patients By Age</h3>

                <FormField label="Age Group">
                  <select
                    value={selectedRange}
                    onChange={(e) => setSelectedRange(e.target.value)}
                    className="input-field"
                  >
                    <option value="">Select Age Group</option>

                    <option value="0-18">0 - 18 Years</option>

                    <option value="19-30">19 - 30 Years</option>

                    <option value="31-45">31 - 45 Years</option>

                    <option value="46-60">46 - 60 Years</option>

                    <option value="60+">60+ Years</option>

                    <option value="custom">Others (Custom)</option>
                  </select>
                </FormField>

                {/* Custom Range */}
                {selectedRange === "custom" && (
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Start Age">
                      <input
                        type="number"
                        placeholder="e.g. 20"
                        value={customAge.start}
                        onChange={(e) =>
                          setCustomAge((p) => ({
                            ...p,
                            start: e.target.value,
                          }))
                        }
                        className="input-field"
                      />
                    </FormField>

                    <FormField label="End Age">
                      <input
                        type="number"
                        placeholder="e.g. 40"
                        value={customAge.end}
                        onChange={(e) =>
                          setCustomAge((p) => ({
                            ...p,
                            end: e.target.value,
                          }))
                        }
                        className="input-field"
                      />
                    </FormField>
                  </div>
                )}

                <button
                  type="button"
                  onClick={applyAgeFilter}
                  className="btn-primary"
                >
                  Select Patients By Age
                </button>
              </div>
            )}

            {/* Search */}
            <FormField
              label={`Search ${
                type === "patient" ? "Patient" : "Delivery Boy"
              }`}
            >
              <input
                type="text"
                placeholder={`Search by ${
                  type === "patient" ? "patient" : "delivery boy"
                } name or ID`}
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                className="input-field"
              />
            </FormField>

            {/* User List */}
            <FormField
              label={`Select ${
                type === "patient" ? "Patients" : "Delivery Boys"
              }`}
              required
            >
              <select
                multiple
                value={form.user_ids}
                onChange={(e) => {
                  const selectedIds = Array.from(
                    e.target.selectedOptions,
                    (option) => Number(option.value),
                  );

                  setForm((p) => ({
                    ...p,
                    user_ids: selectedIds,
                  }));
                }}
                className="input-field h-52"
              >
                {filteredUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {type === "patient"
                      ? `${user.name} (${calculateAge(user.dob)} yrs)`
                      : `${user.name} (${user.mobile})`}
                  </option>
                ))}
              </select>

              <p className="text-xs text-gray-500 mt-1">
                Hold Ctrl (Windows) / Command (Mac) to select multiple users
              </p>
            </FormField>
          </>
        )}

        {/* Title */}
        <FormField label="Title" required>
          <input
            type="text"
            placeholder="Enter title"
            value={form.title}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                title: e.target.value,
              }))
            }
            className="input-field"
          />
        </FormField>

        {/* Description */}
        <FormField label="Description" required>
          <textarea
            rows={4}
            placeholder="Enter notification description"
            value={form.description}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                description: e.target.value,
              }))
            }
            className="input-field"
          />
        </FormField>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="btn-primary flex items-center gap-2"
        >
          <Bell className="w-4 h-4" />

          {loading ? "Sending..." : "Send Notification"}
        </button>
      </form>
    </div>
  );
};
/* ── Time Slots Tab ──────────────────────────────────────────────────────── */
const TimeSlotsTab = () => {
  const { data: slots, loading, refetch } = useFetch(() => timeSlotService.getAll());
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);

  const sorted = useMemo(() => {
    if (!slots) return [];
    return [...slots].sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [slots]);

  const handleSave = async (vals) => {
    try {
      if (modal === 'edit') {
        await timeSlotService.update(selected.id, vals);
        toast.success('Time slot updated!');
      } else {
        await timeSlotService.create({ ...vals, status: 'active', doctorCount: 0 });
        toast.success('Time slot added!');
      }
      refetch(); setModal(null); setSelected(null);
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    const slot = slots?.find(s => s.id === id);
    if (slot?.doctorCount > 0) { toast.error(`Cannot delete — ${slot.doctorCount} doctor(s) using this slot`); return; }
    if (!window.confirm('Delete this time slot?')) return;
    try { await timeSlotService.delete(id); toast.success('Deleted'); refetch(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (slot) => {
    try { await timeSlotService.toggleStatus(slot.id); toast.success('Status updated'); refetch(); }
    catch { toast.error('Failed'); }
  };

  const columns = [
    {
      key: 'label', label: 'Time Slot', render: v => (
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center shrink-0">
            <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white font-mono text-sm">{v}</span>
        </div>
      )
    },
    { key: 'doctorCount', label: 'Doctors', render: v => <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300">{v}</span> },
    {
      key: 'status', label: 'Status', render: (v, row) => (
        <StatusToggleCell active={v === 'active'} onToggle={() => handleToggle(row)} />
      )
    },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => { setSelected(row); setModal('edit'); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" title="Edit">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => handleDelete(row.id)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage doctor availability time slots</p>
        <button onClick={() => { setSelected(null); setModal('add'); }} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Add Time Slot
        </button>
      </div>
      <Table title={`Time Slots (${sorted.length})`} columns={columns} data={sorted} loading={loading} exportFilename="time-slots" />

      <Modal isOpen={modal === 'add' || modal === 'edit'} onClose={() => { setModal(null); setSelected(null); }} title={modal === 'edit' ? 'Edit Time Slot' : 'Add Time Slot'} size="sm">
        <TimeSlotForm initial={modal === 'edit' ? selected : null} onSubmit={handleSave} onClose={() => { setModal(null); setSelected(null); }} />
      </Modal>
    </>
  );
};

/* ── Change Password Tab ─────────────────────────────────────────────────── */
const STAFF_USERS = [
  { id: 2, label: 'Lab',      roleId: 2 },
  { id: 3, label: 'Pharmacy', roleId: 3 },
  { id: 4, label: 'Support',  roleId: 4 },
];

const ChangePasswordTab = () => {
  const [form, setForm]       = useState({ userId: 2, password: '', confirm: '' });
  const [show, setShow]       = useState({ password: false, confirm: false });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.password) { toast.error('Password is required'); return; }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await authService.changePassword(form.userId, form.password);
      toast.success(res?.message || 'Password changed successfully!');
      setForm(p => ({ ...p, password: '', confirm: '' }));
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to change password'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md">
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Change password for Lab, Pharmacy or Support user</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="Select User">
          <select value={form.userId} onChange={e => setForm(p => ({ ...p, userId: +e.target.value }))} className="input-field">
            {STAFF_USERS.map(u => (
              <option key={u.id} value={u.id}>{u.label} (ID: {u.id})</option>
            ))}
          </select>
        </FormField>
        <FormField label="New Password" required>
          <div className="relative">
            <input
              type={show.password ? 'text' : 'password'}
              value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              className="input-field pr-10"
              placeholder="Enter new password"
            />
            <button type="button" onClick={() => setShow(p => ({ ...p, password: !p.password }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {show.password ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </FormField>
        <FormField label="Confirm Password" required>
          <div className="relative">
            <input
              type={show.confirm ? 'text' : 'password'}
              value={form.confirm}
              onChange={e => setForm(p => ({ ...p, confirm: e.target.value }))}
              className="input-field pr-10"
              placeholder="Confirm new password"
            />
            <button type="button" onClick={() => setShow(p => ({ ...p, confirm: !p.confirm }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {show.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </FormField>
        <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 disabled:opacity-50">
          <KeyRound className="w-4 h-4" />{loading ? 'Updating…' : 'Change Password'}
        </button>
      </form>
    </div>
  );
};

/* ── Main Settings Page ──────────────────────────────────────────────────── */
const Settings = () => {
  const [activeTab, setActiveTab] = useState('banners')

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Settings
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage banners, specializations and other.
        </p>
      </div>
      {/* Tab Navigation */}
      {/* <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
              activeTab === key
                ? "border-medihub-primary text-medihub-primary dark:text-medihub-highlight"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div> */}
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2 min-w-max px-1">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 flex-shrink-0 ${
                activeTab === key
                  ? "border-medihub-primary text-medihub-primary dark:text-medihub-highlight"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </button>
          ))}
        </div>
      </div>
      {/* Tab Content */}
      {/* Tab Content */}
      <div>
        {/* {activeTab === "banners" && <BannersTab />} */}

        {activeTab === "offer_banner" && <OfferBannerTab />}
        {activeTab === "prescription_banner" && <PrescriptionBanner />}
        {activeTab === "specializations" && <SpecializationsTab />}
        {activeTab === "qualifications" && <QualificationsTab />}
        {activeTab === "change_password" && <ChangePasswordTab />}
        {activeTab === "coupon" && <CouponTab />}
        {activeTab === "user_notification" && (
          <NotificationTab type="patient" />
        )}

        {activeTab === "Delivery_notification" && (
          <NotificationTab type="delivery" />
        )}
      </div>{" "}
      <div>
        {activeTab === "banners" && <BannersTab />}
        {activeTab === "specializations" && <SpecializationsTab />}
        {activeTab === "qualifications" && <QualificationsTab />}
        {activeTab === "change_password" && <ChangePasswordTab />}
      </div>
    </div>
  );
};

export default Settings;
